import React, { useEffect, useRef, useState } from "react";
import { useLoyaltySession } from "../../hooks/useLoyaltySession";

interface LinkedEvent {
    path: "linked" | "enrolled";
    imported: boolean;
    // Uniquely identifies a link/enroll event so re-linking triggers a
    // fresh toast even if the path is the same as last time.
    linkedAt: string;
}

interface LoyaltySyncToastProps {
    linkedEvent: LinkedEvent | null;
}

interface ToastMessage {
    title: string;
    subtitle: string;
}

const AUTO_DISMISS_MS = 7000;

function linkedMessage(event: LinkedEvent): ToastMessage {
    if (event.path === "enrolled") {
        return {
            title: "Welcome to Mabuhay Miles",
            subtitle: "Your account was created from your Movmo profile.",
        };
    }
    return {
        title: "Mabuhay Miles linked",
        subtitle: event.imported
            ? "Your miles and tier are saved to your Movmo account."
            : "Signed in via Movmo.",
    };
}

/**
 * Bottom-LEFT toast (MOVMO-541 Layer 3). Two triggers:
 *   1. Explicit — parent passes a fresh linkedEvent right after the consent
 *      flow returns (via the callback's one-shot flag) or after the on-site
 *      late-link modal completes. Copy varies by path + import consent.
 *   2. Derived — sessionActive transitions false -> true on a page where
 *      the airline-side link ALREADY existed at mount (the session read finishes
 *      after load, or the Movmo chip re-grants after a revoke). Skipped on
 *      initial mount so a normal reload with an already-active session
 *      stays quiet. After an explicit linkedEvent the restore toast is
 *      suppressed (the post-consent landing would otherwise double-toast),
 *      but only until the next revoke — a session drop disarms the
 *      suppression so revoke→re-grant always toasts.
 */
const LoyaltySyncToast: React.FC<LoyaltySyncToastProps> = ({
    linkedEvent,
}) => {
    const { sessionActive, link } = useLoyaltySession();
    const [message, setMessage] = useState<ToastMessage | null>(null);
    const dismissTimer = useRef<number | null>(null);
    const prevSessionActive = useRef(sessionActive);
    const linkExistedAtMount = useRef(link !== null);
    const seenLinkedAt = useRef<string | null>(null);
    const suppressRestore = useRef(false);

    const show = (msg: ToastMessage) => {
        setMessage(msg);
        if (dismissTimer.current !== null) {
            window.clearTimeout(dismissTimer.current);
        }
        dismissTimer.current = window.setTimeout(
            () => setMessage(null),
            AUTO_DISMISS_MS,
        );
    };

    useEffect(
        () => () => {
            if (dismissTimer.current !== null) {
                window.clearTimeout(dismissTimer.current);
            }
        },
        [],
    );

    useEffect(() => {
        if (!linkedEvent) return;
        if (seenLinkedAt.current === linkedEvent.linkedAt) return;
        seenLinkedAt.current = linkedEvent.linkedAt;
        suppressRestore.current = true;
        show(linkedMessage(linkedEvent));
    }, [linkedEvent]);

    useEffect(() => {
        const wasActive = prevSessionActive.current;
        prevSessionActive.current = sessionActive;
        if (wasActive && !sessionActive) {
            // A revoke starts a new cycle: the suppression only exists to
            // de-dupe the post-consent landing's initial activation, so the
            // next restore after a real revoke must toast again (review
            // finding: a sticky latch swallowed the demo's revoke→restore
            // beat whenever it followed a link on the same pageload).
            suppressRestore.current = false;
        }
        if (
            !wasActive &&
            sessionActive &&
            linkExistedAtMount.current &&
            !suppressRestore.current
        ) {
            show({
                title: "Welcome back to Mabuhay Miles",
                subtitle: "Signed in via Movmo.",
            });
        }
    }, [sessionActive]);

    if (!message) return null;

    return (
        <div
            className="fixed bottom-6 left-6 z-40 w-full rounded-lg border-l-4 bg-white shadow-lg"
            style={{ maxWidth: 340, borderLeftColor: "#0047ba" }}
        >
            <div className="flex items-start gap-3 px-4 py-3">
                <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-900">
                        ✓ {message.title}
                    </p>
                    <p className="mt-0.5 text-xs text-gray-600">
                        {message.subtitle}
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => setMessage(null)}
                    className="text-gray-400 hover:text-gray-600"
                    aria-label="Dismiss"
                >
                    ×
                </button>
            </div>
        </div>
    );
};

export default LoyaltySyncToast;
